import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LAYER_MODES, PANEL_VALUES, STATUS_VALUES } from '../utils/mapUrlParams';
import { isMapPath, presetToParams, resetMapParams } from '../utils/mapParamsState';

export const MapParamsContext = createContext(null);

/**
 * Owned by DashboardLayout. Params are active only while pathname is /map and always start
 * empty. A query string on /map is discarded; in-app links pass an allow-listed
 * `location.state.mapPreset`, applied once and then cleared.
 */
export const useMapParamsState = (location, navigate, knownCommodities) => {
  const { pathname, search, state } = location;
  const active = isMapPath(pathname);
  const [params, setParams] = useState(() => new URLSearchParams());

  useEffect(() => {
    if (!active) {
      setParams(resetMapParams);
      return;
    }
    if (!search && !state?.mapPreset) return;
    if (state?.mapPreset) setParams(presetToParams(state.mapPreset, {
        commodities: knownCommodities,
        layers: LAYER_MODES,
        panels: PANEL_VALUES,
        statuses: STATUS_VALUES,
      }));
    navigate(pathname, { replace: true });
  }, [active, pathname, search, state, knownCommodities, navigate]);

  const set = useCallback((next) => {
    setParams((prev) => new URLSearchParams(typeof next === 'function' ? next(prev) : next));
  }, []);

  return useMemo(
    () => ({ active, params, set }),
    [active, params, set],
  );
};

/**
 * Drop-in for react-router's useSearchParams on /map. The setter ignores the
 * `{ replace }` option. Elsewhere it falls through to the URL.
 */
const useMapParams = () => {
  const ctx = useContext(MapParamsContext);
  const routerResult = useSearchParams();
  return ctx && ctx.active ? [ctx.params, ctx.set] : routerResult;
};

export default useMapParams;
